import React, { useEffect, useState } from "react";
import { Drawer, Menu } from "antd";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
   FaHome,
   FaUsers,
   FaCalendarAlt,
   FaChalkboardTeacher,
   FaClipboardList,
   FaVideo,
   FaBookOpen,
   FaHandPointUp,
} from "react-icons/fa";
import { CgClose } from "react-icons/cg";
import { BsPerson, BsPersonX, BsPersonPlus } from "react-icons/bs";
import "./header.style.scss";

interface NavDrawerProps {
   open: boolean;
   onClose: () => void;
}

export const NavDrawer: React.FC<NavDrawerProps> = ({ open, onClose }) => {
   const navigate = useNavigate();
   const location = useLocation();
   const [isLogged, setIsLogged] = useState<boolean>(false);

   const { SubMenu } = Menu;
   const isPPSection = location.pathname.startsWith("/pp");

   useEffect(() => {
      const user = localStorage.getItem("user");
      setIsLogged(!!user);
   }, [open]);

   const handleLogout = (): void => {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.clear();
      setIsLogged(false);
      window.location.replace("https://www.medellin.gov.co/es/secretaria-cultura-ciudadana/estimulos-cultura/");
   };

   const onNavigateDrawer = (): void => {
      onClose();
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
   };

   const getCurrentMenuKey = (): string => {
      const path = location.pathname;
      if (path === "/" || path === "/estimuloscultura" || path === "/estimuloscultura/") return "convocatorias";
      if (path.includes("/beneficiarios")) return "beneficiarios";
      if (path.includes("/agenda")) return "agenda";
      if (path.includes("/mentorias")) return "mentorias";
      if (path.includes("/form")) return "form";
      if (path.includes("/miradasProgramacion")) return "miradas-programacion";
      if (path.includes("/miradasIndustria")) return "miradas-industria";
      if (path.includes("/miradasPrensa")) return "miradas-prensa";
      if (path.includes("/cinematecaDistrital")) return "miradas-cinemateca";
      if (path.includes("/miradas")) return "miradas-home";
      if (path.includes("/lep")) return "lep";
      if (path.includes("/ppestimulos")) return "ppestimulos";
      if (path.includes("/ppformacion")) return "ppformacion";
      if (path.includes("/ppfortalecimiento")) return "ppfortalecimiento";
      if (path.includes("/ppagenda")) return "ppagenda";
      if (path.includes("/pp")) return "pp";
      return "convocatorias";
   };

   const currentKey = getCurrentMenuKey();

   return (
      <Drawer
         className="alcaldia-nav-drawer"
         width={340}
         placement="left"
         onClose={onClose}
         visible={open}
         closeIcon={<CgClose size={22} />}
         zIndex={1300}
      >
         <div className="drawer-menu-wrapper">
            <div className="drawer-auth-section">
               {isLogged ? (
                  <button
                     type="button"
                     className="drawer-auth-btn logout"
                     onClick={() => {
                        onClose();
                        handleLogout();
                     }}
                  >
                     <BsPersonX size={18} />
                     <span>Cerrar sesión</span>
                  </button>
               ) : (
                  <>
                     <button
                        type="button"
                        className="drawer-auth-btn login"
                        onClick={() => {
                           onClose();
                           navigate("/login");
                        }}
                     >
                        <BsPerson size={18} />
                        <span>Iniciar sesión</span>
                     </button>
                     <button
                        type="button"
                        className="drawer-auth-btn register"
                        onClick={() => {
                           onClose();
                           window.location.href = "https://www.medellin.gov.co/registrate";
                        }}
                     >
                        <BsPersonPlus size={18} />
                        <span>Regístrate</span>
                     </button>
                  </>
               )}
            </div>

            <div className="drawer-nav-list">
               {isPPSection ? (
                  <ul className="drawer-links">
                     <li>
                        <NavLink
                           to="/pp"
                           className={currentKey === "pp" ? "active" : ""}
                           onClick={onNavigateDrawer}
                        >
                           <FaHome className="drawer-menu-icon" />
                           <span>Inicio PP</span>
                        </NavLink>
                     </li>
                     <li>
                        <NavLink
                           to="/ppestimulos"
                           className={currentKey === "ppestimulos" ? "active" : ""}
                           onClick={onNavigateDrawer}
                        >
                           <FaHandPointUp className="drawer-menu-icon" />
                           <span>Estímulos PP</span>
                        </NavLink>
                     </li>
                     <li>
                        <NavLink
                           to="/ppformacion"
                           className={currentKey === "ppformacion" ? "active" : ""}
                           onClick={onNavigateDrawer}
                        >
                           <FaChalkboardTeacher className="drawer-menu-icon" />
                           <span>Formación</span>
                        </NavLink>
                     </li>
                     <li>
                        <NavLink
                           to="/ppfortalecimiento"
                           className={currentKey === "ppfortalecimiento" ? "active" : ""}
                           onClick={onNavigateDrawer}
                        >
                           <FaUsers className="drawer-menu-icon" />
                           <span>Fortalecimiento</span>
                        </NavLink>
                     </li>
                     <li>
                        <NavLink
                           to="/ppagenda"
                           className={currentKey === "ppagenda" ? "active" : ""}
                           onClick={onNavigateDrawer}
                        >
                           <FaCalendarAlt className="drawer-menu-icon" />
                           <span>Agenda PP</span>
                        </NavLink>
                     </li>
                     <li className="divider-item" />
                     <li>
                        <NavLink to="/" onClick={onNavigateDrawer}>
                           <FaHome className="drawer-menu-icon" />
                           <span>Estímulos Cultura</span>
                        </NavLink>
                     </li>
                  </ul>
               ) : (
                  <Menu
                     mode="inline"
                     className="drawer-antd-menu"
                     selectedKeys={[currentKey]}
                     defaultOpenKeys={["miradas"]}
                  >
                     <Menu.Item key="convocatorias" icon={<FaHome className="drawer-menu-icon" />}>
                        <NavLink to="/" onClick={onNavigateDrawer}>
                           Convocatorias
                        </NavLink>
                     </Menu.Item>
                     <Menu.Item key="beneficiarios" icon={<FaUsers className="drawer-menu-icon" />}>
                        <NavLink to="/beneficiarios" onClick={onNavigateDrawer}>
                           Beneficiarios
                        </NavLink>
                     </Menu.Item>
                     <Menu.Item key="agenda" icon={<FaCalendarAlt className="drawer-menu-icon" />}>
                        <NavLink to="/agenda" onClick={onNavigateDrawer}>
                           Agenda cultural
                        </NavLink>
                     </Menu.Item>
                     <Menu.Item key="mentorias" icon={<FaChalkboardTeacher className="drawer-menu-icon" />}>
                        <NavLink to="/mentorias" onClick={onNavigateDrawer}>
                           Mentorías
                        </NavLink>
                     </Menu.Item>
                     <Menu.Item key="form" icon={<FaClipboardList className="drawer-menu-icon" />}>
                        <NavLink to="/form" onClick={onNavigateDrawer}>
                           Formularios
                        </NavLink>
                     </Menu.Item>
                     <SubMenu
                        key="miradas"
                        title="Miradas"
                        icon={<FaVideo className="drawer-menu-icon" />}
                     >
                        <Menu.Item key="miradas-home">
                           <NavLink to="/miradas" onClick={onNavigateDrawer}>
                              Home
                           </NavLink>
                        </Menu.Item>
                        <Menu.Item key="miradas-programacion">
                           <NavLink to="/miradasProgramacion" onClick={onNavigateDrawer}>
                              Programación
                           </NavLink>
                        </Menu.Item>
                        <Menu.Item key="miradas-industria">
                           <NavLink to="/miradasIndustria" onClick={onNavigateDrawer}>
                              Industria
                           </NavLink>
                        </Menu.Item>
                        <Menu.Item key="miradas-prensa">
                           <NavLink to="/miradasPrensa" onClick={onNavigateDrawer}>
                              Prensa
                           </NavLink>
                        </Menu.Item>
                        <Menu.Item key="miradas-cinemateca">
                           <a
                              href="https://www.medellin.gov.co/irj/portal/medellin?NavigationTarget=contenido/11374-Cinemateca-Municipal-de-Medellin"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={onClose}
                           >
                              Cinemateca Distrital
                           </a>
                        </Menu.Item>
                     </SubMenu>
                     <Menu.Item key="lep" icon={<FaBookOpen className="drawer-menu-icon" />}>
                        <NavLink to="/lep" onClick={onNavigateDrawer}>
                           LEP
                        </NavLink>
                     </Menu.Item>
                     <Menu.Item key="pp" icon={<FaHandPointUp className="drawer-menu-icon" />}>
                        <NavLink to="/pp" onClick={onNavigateDrawer}>
                           Presupuesto Participativo (PP)
                        </NavLink>
                     </Menu.Item>
                  </Menu>
               )}
            </div>
         </div>
      </Drawer>
   );
};

export default NavDrawer;
